import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Info, AlertTriangle, ChevronLeft, ChevronRight, Lightbulb } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DocsArticle, DocsCard, DocsStep } from '@/routes/docs/_data'

interface DocsContentProps {
  article: DocsArticle
  allArticles: DocsArticle[]
}

function CardGrid({ cards }: { cards: DocsCard[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
      {cards.map((card) => {
        const Icon = card.icon
        return (
          <Link
            key={card.href}
            to={card.href}
            className="group flex gap-3.5 p-4 rounded-xl border border-border/60 hover:border-border hover:bg-accent/30 transition-all duration-200"
          >
            <div className="shrink-0 mt-0.5">
              <div className="w-8 h-8 rounded-lg bg-accent/80 flex items-center justify-center">
                <Icon className="h-4 w-4 text-foreground/70" />
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[14px] font-medium text-foreground group-hover:text-foreground transition-colors">
                {card.title}
              </div>
              <div className="text-[12.5px] text-muted-foreground/70 mt-0.5 leading-relaxed">
                {card.description}
              </div>
            </div>
          </Link>
        )
      })}
    </div>
  )
}

function StepList({ steps }: { steps: DocsStep[] }) {
  return (
    <div className="mt-5 space-y-0">
      {steps.map((step, i) => (
        <div key={i} className="flex gap-4 pb-6 last:pb-0">
          <div className="flex flex-col items-center shrink-0">
            <div className="w-7 h-7 rounded-full bg-foreground text-background flex items-center justify-center text-[12px] font-semibold shrink-0">
              {i + 1}
            </div>
            {i < steps.length - 1 && (
              <div className="w-px flex-1 bg-border/60 mt-2" />
            )}
          </div>
          <div className="pt-0.5 pb-2 min-w-0">
            <div className="text-[14px] font-semibold text-foreground">{step.title}</div>
            <div className="text-[14px] text-muted-foreground mt-1 leading-relaxed">{step.content}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

function TableOfContents({ article }: { article: DocsArticle }) {
  const [activeId, setActiveId] = useState<string>('')

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          setActiveId(visible[0].target.id)
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    )

    article.sections.forEach((section) => {
      const el = document.getElementById(section.id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [article.sections])

  if (article.sections.length < 3) return null

  return (
    <div className="hidden xl:block w-52 shrink-0">
      <div className="sticky top-24 py-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/60 mb-3">
          On this page
        </p>
        <ul className="space-y-1.5 border-l border-border/40">
          {article.sections.map((section) => (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                onClick={(e) => {
                  e.preventDefault()
                  document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' })
                }}
                className={cn(
                  'block pl-3 py-0.5 text-[12px] leading-snug border-l -ml-px transition-colors',
                  activeId === section.id
                    ? 'border-foreground text-foreground font-medium'
                    : 'border-transparent text-muted-foreground/70 hover:text-muted-foreground'
                )}
              >
                {section.heading}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export function DocsContent({ article, allArticles }: DocsContentProps) {
  const currentIndex = allArticles.findIndex((a) => a.slug === article.slug)
  const prev = currentIndex > 0 ? allArticles[currentIndex - 1] : null
  const next = currentIndex < allArticles.length - 1 ? allArticles[currentIndex + 1] : null
  const Icon = article.icon

  return (
    <div className="flex gap-0">
      <div className="flex-1 min-w-0 px-6 md:px-12 lg:px-16 py-8 md:py-10 max-w-4xl">
        <header className="mb-10">
          <div className="flex items-center gap-3.5 mb-3">
            <div className="w-10 h-10 rounded-lg bg-accent/80 flex items-center justify-center">
              <Icon className="h-5 w-5 text-foreground/70" />
            </div>
            <h1 className="text-3xl md:text-[34px] font-semibold tracking-tight leading-tight">{article.title}</h1>
          </div>
          <p className="text-[16px] text-muted-foreground leading-relaxed">{article.description}</p>
        </header>

        <div>
          {article.sections.map((section, idx) => (
            <div key={section.id}>
              <h2
                id={section.id}
                className={cn(
                  'text-lg font-semibold mb-2 scroll-mt-24 tracking-tight',
                  idx === 0 ? 'mt-0' : 'mt-8'
                )}
              >
                {section.heading}
              </h2>
              <div className="text-[15px] text-muted-foreground leading-[1.7]">
                {section.content}
              </div>

              {section.bullets && (
                <ul className="mt-4 space-y-2.5 pl-0">
                  {section.bullets.map((bullet, i) => {
                    const hasDash = bullet.includes(' — ')
                    const [label, desc] = hasDash ? bullet.split(' — ') : [null, bullet]
                    return (
                      <li key={i} className="flex gap-3 text-[14px] text-muted-foreground leading-relaxed">
                        <span className="shrink-0 mt-[7px] w-1.5 h-1.5 rounded-full bg-muted-foreground/30" />
                        <span>
                          {label ? (
                            <><span className="font-medium text-foreground/90">{label}</span> — {desc}</>
                          ) : desc}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              )}

              {section.steps && <StepList steps={section.steps} />}
              {section.cards && <CardGrid cards={section.cards} />}

              {section.tip && (
                <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-4 mt-5 flex gap-3">
                  <div className="shrink-0 mt-0.5">
                    <div className="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center">
                      <Lightbulb className="h-3.5 w-3.5 text-blue-400" />
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-400/80 mb-1">Tip</p>
                    <p className="text-[13px] text-muted-foreground leading-relaxed">{section.tip}</p>
                  </div>
                </div>
              )}

              {section.note && (
                <div className="bg-accent/40 border border-border/60 rounded-xl p-4 mt-5 flex gap-3">
                  <div className="shrink-0 mt-0.5">
                    <div className="w-6 h-6 rounded-md bg-foreground/5 flex items-center justify-center">
                      <Info className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60 mb-1">Note</p>
                    <p className="text-[13px] text-muted-foreground leading-relaxed">{section.note}</p>
                  </div>
                </div>
              )}

              {section.warning && (
                <div className="bg-yellow-500/5 border border-yellow-500/15 rounded-xl p-4 mt-5 flex gap-3">
                  <div className="shrink-0 mt-0.5">
                    <div className="w-6 h-6 rounded-md bg-yellow-500/10 flex items-center justify-center">
                      <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-yellow-500/80 mb-1">Warning</p>
                    <p className="text-[13px] text-muted-foreground leading-relaxed">{section.warning}</p>
                  </div>
                </div>
              )}

              {section.image && (
                <figure className="mt-6">
                  <div className="rounded-xl border border-border/60 overflow-hidden bg-muted/30">
                    <img
                      src={section.image.src}
                      alt={section.image.alt}
                      className="w-full"
                      loading="lazy"
                    />
                  </div>
                  {section.image.caption && (
                    <figcaption className="text-[12px] text-muted-foreground/50 text-center mt-2.5">
                      {section.image.caption}
                    </figcaption>
                  )}
                </figure>
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-4 border-t border-border/40 pt-8 mt-16">
          {prev ? (
            <Link
              to={`/docs/${prev.slug}`}
              className="flex flex-col gap-1 p-4 rounded-xl border border-border/50 hover:border-border hover:bg-accent/20 transition-all group"
            >
              <div className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground/40">
                <ChevronLeft className="h-3 w-3" />
                Previous
              </div>
              <div className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">{prev.title}</div>
            </Link>
          ) : (
            <div />
          )}
          {next ? (
            <Link
              to={`/docs/${next.slug}`}
              className="flex flex-col gap-1 p-4 rounded-xl border border-border/50 hover:border-border hover:bg-accent/20 transition-all group text-right items-end"
            >
              <div className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground/40">
                Next
                <ChevronRight className="h-3 w-3" />
              </div>
              <div className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">{next.title}</div>
            </Link>
          ) : (
            <div />
          )}
        </div>
      </div>
      <TableOfContents article={article} />
    </div>
  )
}
